import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CustomInput } from 'reactstrap';
import clsx from 'clsx';
import SignatureCanvas from 'react-signature-canvas';
import { Form, Field } from 'react-final-form';
import { Topography, Input, InputLabel, InputError, Button, Select } from 'components/ui';
import { Validators, Parsers, dataURLtoFile, filesToBase64 } from 'utils';
import DatePickerUI from 'components/ui/DatePicker/DatePicker';
import styles from './RequestSignatureSection.module.scss';
import { SelectItem } from 'components/ui/Select/Select';
import { AuthFormValues } from 'types/requestItem';
import { authSelector } from 'store/reducers/auth.reducer';
import moment from 'moment';
import { CreateRequestHeader } from 'components/grid';
import i18n from 'i18next';
import Spinner from 'components/ui/Spinner/Spinner';
import { Radio } from 'components/ui';
type RequestSignatureSectionProps = {
  langStates: [string, React.Dispatch<React.SetStateAction<string>>];
  translate: (key: string) => string;
  className?: string;
  onSubscribe?: (token: AuthFormValues) => void;
  request?: any;
  dependent?: any;
  loadingStates: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  selectedSensitiveInformationOptions?: any;
  
};

export const RequestSignatureSection: React.FC<RequestSignatureSectionProps> = ({
  className,
  onSubscribe,
  request,
  langStates,
  translate,
  loadingStates,
}) => {
  const [birthDay, setBirthDay] = useState<Date | undefined>();
  const [vocation, setVocation] = useState<SelectItem>();
  const [initialValues, setInitialValues] = useState({});
  const [authName, setAuthName] = useState<string>('');
  const [showExtraField, setShowExtraField] = useState<boolean>(false);
  const [extraValue, setExtraValue] = useState<string>('');
  const [authInitials, setAuthInitials] = useState<string>('');
  const [isDrawMode, setDrawMode] = useState<boolean>(false);
  const [isSigned, setSigned] = useState<boolean>(false);
  const [, setSelectedLanguage] = langStates;
  const [loading, setLoading] = loadingStates;
  const signRef = useRef<SignatureCanvas>(null);

  const { profile } = useSelector(authSelector);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [selectedSensitiveInformationOptions, setselectedSensitiveInformationOptions] = useState<string[]>([]);
  const sensitiveInformationOptions = request?.provider?.patient_e_authorization_type;

 // Handle radio change
const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const value = event.target.value;
  setSelectedValue(value);

  if (value === 'Yes') {
    // Check all checkboxes if "Yes" is selected
    setselectedSensitiveInformationOptions(sensitiveInformationOptions);
  } else {
    // Uncheck all checkboxes if "No" is selected
    setselectedSensitiveInformationOptions([]);
  }
};
// Handle checkbox change
const handleCheckboxChange = (option: string) => {
  const updatedCheckedBoxes = selectedSensitiveInformationOptions.includes(option)
    ? selectedSensitiveInformationOptions.filter(item => item !== option) // Uncheck the checkbox
    : [...selectedSensitiveInformationOptions, option]; // Check the checkbox

  setselectedSensitiveInformationOptions(updatedCheckedBoxes);

  // If all checkboxes are unchecked, uncheck the "Yes" radio button
  if (updatedCheckedBoxes.length === 0) {
    setSelectedValue('No');
  }else{
    setSelectedValue('Yes');
  }
};
 
  const vocations: SelectItem[] = [
    {
      label: translate('text-patient'),
      value: 'Patient',
    },
    {
      label: translate('text-guardian'),
      value: 'Guardian',
      extraField: translate('text-guardian-extra-feild'),
    },
    {
      label: translate('text-patient-poa'),
      value: 'Patient Representative - Power of Attorney',
      extraField: translate('text-patient-poa-extra-text'),
    },
    {
      label: translate('text-patient-nok'),
      value: 'Patient Representative - Next of Kin',
      extraField: translate('text-guardian-nok-extra-text'),
    },
    {
      label: translate('text-patient-dp'),
      value: 'Patient Representative - Deceased Patient',
      extraField: translate('text-guardian-dp-extra-text'),
    },
    {
      label: translate('text-patient-eb'),
      value: 'Patient Representative - Executor/Beneficiary',
      extraField: translate('text-guardian-eb-extra-text'),
    },
  ];

  useEffect(() => {
    const display_lang: any = localStorage.getItem('language');
    i18n.changeLanguage(display_lang);
    const vocations: SelectItem[] = [
      {
        label: translate('text-patient'),
        value: 'Patient',
      },
      {
        label: translate('text-guardian'),
        value: 'Guardian',
        extraField: translate('text-guardian-extra-feild'),
      },
      {
        label: translate('text-patient-poa'),
        value: 'Patient Representative - Power of Attorney',
        extraField: translate('text-patient-poa-extra-text'),
      },
      {
        label: translate('text-patient-nok'),
        value: 'Patient Representative - Next of Kin',
        extraField: translate('text-guardian-nok-extra-text'),
      },
      {
        label: translate('text-patient-dp'),
        value: 'Patient Representative - Deceased Patient',
        extraField: translate('text-guardian-dp-extra-text'),
      },
      {
        label: translate('text-patient-eb'),
        value: 'Patient Representative - Executor/Beneficiary',
        extraField: translate('text-guardian-eb-extra-text'),
      },
    ];
    if (request.auth?.birthday || request.patientInfo?.someoneElse || request.patientInfo?.patient_dob)
      setBirthDay(new Date(request.auth?.birthday || request?.patientInfo?.patient_dob));
    if (request.auth?.iAmThe) {
      setVocation(vocations.find(item => item.value == request.auth.iAmThe));
      if (vocations.slice(1).some(i => i.value == request.auth.iAmThe)) {
        setShowExtraField(true);
        setExtraValue(request.auth?.extraValue || '');
      } else {
        setShowExtraField(false);
        setExtraValue('');
      }
      setVocation(vocations[0]);
    } else {
      setVocation(vocations[0]);
    }
    setAuthName(
      request.auth?.fullName ||
        (request.patientInfo?.someoneElse &&
          `${request.patientInfo?.patient_first_name} ${request.patientInfo?.patient_middle_name ? request.patientInfo?.patient_middle_name : ''} ${request.patientInfo?.patient_last_name}`) ||
        `${request.patientInfo?.patient_first_name} ${request.patientInfo?.patient_middle_name ? request.patientInfo?.patient_middle_name : ''} ${request.patientInfo?.patient_last_name}` ||
        '',
    );
    setAuthInitials(request.auth?.initials || '');
    setInitialValues({
      birthday: birthDay,
      ssn: request.auth?.ssn || request?.patientInfo?.auth_ssn || '',
      iAmThe: request.auth?.iAmThe && vocations.find(item => item.value === request.auth.iAmThe),
    });
    if (request.auth?.isDrawMode) setDrawMode(request.auth?.isDrawMode || false);
  }, []);

  useEffect(() => {
    if (isDrawMode && request.auth?.isDrawMode && request.auth?.signature) {
      filesToBase64([request.auth.signature]).then(base64 => {
        setTimeout(() => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          signRef?.current?.fromDataURL(base64)
          setSigned(true)
        }, 1000)
      })
    }
  }, [isDrawMode, request.auth]);

  function toCheck(value: any) {
    if (vocations.slice(1).some(i => i.value == value)) {
      return true;
    }
    return false;
  }
  function onSubmit(values: AuthFormValues) {
    // Include selected radio value
    const sensitiveInformation = selectedValue;
    // Include selected checkboxes
    const sensitiveOptions = selectedSensitiveInformationOptions;
    if (request?.authorizations == undefined) {
    } else {
      if (!request.authorizations[0]?.is_upload_authorization) {
        request.authorizations.shift();
      }
    }
    const dataTest = toCheck(values.iAmThe);
    if (!dataTest) {
      delete values.extraValue;
    } else values.extraField = vocation?.extraField;

      // Add sensitive information to the data being sent
    const dataToSend = {
      ...values,
      sensitiveInformation,
      sensitiveOptions,
    };

    if (isDrawMode) {
      const base64 = signRef?.current?.toDataURL('image/png');
      const file = dataURLtoFile(base64 ?? '', 'image/png', 'signature.jpeg');
      onSubscribe && onSubscribe({ ...values, iAmThe: vocations.find(item => item.value == values.iAmThe), signature: file, isDrawMode });
      setLoading(false);
    } else {
      const canvas: any = document.getElementById('signatureCanvas');
      const ctx = canvas.getContext('2d');
      ctx.font = '36px HomemadeApple';
      ctx.fillText(`${showExtraField ? extraValue : values.fullName}, ${values.initials}`, 10, 50);
      const base64 = canvas.toDataURL('image/png', 1);
      const file = dataURLtoFile(base64, 'image/png', 'signature.jpeg');
      onSubscribe &&
        onSubscribe({
          ...values,
          iAmThe: vocations.find(item => item.value == values.iAmThe),
          signature: file,
          isDrawMode,
          sensitiveInformation,
          sensitiveOptions
        });
      setLoading(false);
    }
  }

  function handleChangeBirthDay(value: Date) {
    setBirthDay(value);
  }

  function handleSelect(item: any) {
    if (vocations.slice(1).some(i => i.value == item.value)) {
      setShowExtraField(true);
    } else {
      setShowExtraField(false);
    }
    setVocation(item);
  }

  const handler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    localStorage.setItem('language', '');
    setSelectedLanguage(value);
    i18n.changeLanguage(event.target.value);
    localStorage.setItem('language', value);
    const vocations: SelectItem[] = [
      {
        label: translate('text-patient'),
        value: 'Patient',
      },
      {
        label: translate('text-guardian'),
        value: 'Guardian',
        extraField: translate('text-guardian-extra-feild'),
      },
      {
        label: translate('text-patient-poa'),
        value: 'Patient Representative - Power of Attorney',
        extraField: translate('text-patient-poa-extra-text'),
      },
      {
        label: translate('text-patient-nok'),
        value: 'Patient Representative - Next of Kin',
        extraField: translate('text-guardian-nok-extra-text'),
      },
      {
        label: translate('text-patient-dp'),
        value: 'Patient Representative - Deceased Patient',
        extraField: translate('text-guardian-dp-extra-text'),
      },
      {
        label: translate('text-patient-eb'),
        value: 'Patient Representative - Executor/Beneficiary',
        extraField: translate('text-guardian-eb-extra-text'),
      },
    ];
    setVocation(vocations[0]);
  };

  const clearSignature = () =>{
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    signRef?.current?.clear()
    setSigned(false)
  }

  const langOption = request.provider.multi_language_for_patient_eauth
    ? request.provider.multi_language_for_patient_eauth
    : request.custodian_selected_language;

    const handleInputChange = (e:any) => {
      const inputValue = e.target.value;
      if (/^[A-Za-z ]+$/.test(inputValue)) {
        setAuthInitials(inputValue);
      }
    };
  return (
    <div className={clsx(styles.root, className, 'row')} id="authorization_PDF_HTML_container">
      <div className={clsx(styles.block, 'col-12')}>
        <CreateRequestHeader title={translate('heading')} />
        <br />
        <br />
        <br />
        {langOption?.length >=2 ? (
          <div className="lag_drop_down">
            Language
            <select onChange={handler}>
              {langOption.map((option: any) => (
                <option value={option.value}>{option.key}</option>
              ))}
            </select>
          </div>
        ) : null}
        <Topography.H4>{request?.provider?.name}</Topography.H4>
        {request?.provider?.address},<br />
        {request?.provider?.city}, {request?.provider?.state} {request?.provider?.zipcode}
      </div>
      <div className={clsx(styles.block, 'col-12')}>
        <Topography.H4>{translate('Sub-heading-one')}</Topography.H4>
        <p>{translate('sub-heading-p-one')}</p>
         {request?.provider?.sensitive_information_options_for_patient_e_authorization ? null :( <p>{translate('sub-heading-p-two')}</p>)}        
        <p>{translate('sub-heading-p-three')}</p>
      </div>
      <div className="col-12">
        <Topography.H4 className="mb-0">{translate('form-heading')}</Topography.H4>
        <div>{translate('form-sub-heading')}</div>
        <Form
          initialValues={initialValues}
          onSubmit={values => {
            setLoading(true);
            setTimeout(() => {
              onSubmit(values);
            }, 100);
          }}
          render={({ values, handleSubmit, hasValidationErrors }) => (
            <form onSubmit={handleSubmit} className="mt-3">
              <div className="row">
                <Field name="fullName" validate={Validators.required}>
                  {({ input, meta }) => {
                    input.onChange(authName);
                    return (
                      <div className="col-12 col-md-4 mb-3">
                        <InputLabel text={translate('text-name-label')} />
                        <Input
                          {...input}
                          onChange={(e: any) => setAuthName(e?.target?.value || '')}
                          type="text"
                          hasError={meta.error && meta.touched}
                        />
                        {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                      </div>
                    );
                  }}
                </Field>

                <Field name="initials" validate={Validators.compose(Validators.required, Validators.validInitialsLimit)}>
                  {({ input, meta }) => {
                    input.onChange(authInitials);
                    return (
                      <div className="col-12 col-md-4 mb-3">
                        <InputLabel text={translate('text-initials-label')} />
                        <Input
                          {...input}
                          onChange={handleInputChange}
                          type="text"
                          hasError={meta.error && meta.touched}
                          maxLength={20}
                          pattern="[A-Za-z ]+"
                        />
                        {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                      </div>
                    );
                  }}
                </Field>

                <Field
                  name="birthday"
                  validate={Validators.compose(Validators.required, Validators.pastDate)}
                  render={({ input, meta }) => {
                    input.onChange(birthDay && moment(birthDay).format(process.env.REACT_APP_DATE_FORMAT));
                    return (
                      <div className="col-12 col-md-4 mb-3">
                        <InputLabel text={translate('text-date-label')} />
                        <DatePickerUI
                          onChange={handleChangeBirthDay}
                          withLabel={true}
                          value={birthDay}
                          futureDateNotAllowed={true}
                        >
                          {meta.error && birthDay && <InputError>{meta.error}</InputError>}
                        </DatePickerUI>
                      </div>
                    );
                  }}
                />
                <Field name="ssn" validate={Validators.validSnn} parse={Parsers.ssn}>
                  {({ input, meta }) => (
                    <div className="col-12 col-md-4 mb-3">
                      <InputLabel text={translate('text-ssn-label')} />
                      <Input {...input} type="text" hasError={meta.error && meta.touched} />
                      {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                    </div>
                  )}
                </Field>
                <Field
                  name="iAmThe"
                  validate={Validators.required}
                  render={({ input }) => {
                    input.onChange(vocation && vocation.value);
                    return (
                      <div className="col-12 col-md-4 mb-3  i-am-select-section">
                        <InputLabel className="textLabel" text={translate('text-patient-type-label')} />
                        <Select
                          id="inputSelect"
                          options={vocations}
                          onChange={handleSelect}
                          value={vocation}
                          placeholder=""
                        />
                      </div>
                    );
                  }}
                />
                {vocation?.extraField && (
                  <Field name="extraValue" validate={Validators.required}>
                    {({ input, meta }) => {
                      input.onChange(extraValue);
                      return (
                        <div className="col-12 col-md-4 mb-3">
                          <InputLabel text={vocation?.extraField + ' *'} />
                          <Input
                            {...input}
                            onChange={(e: any) => setExtraValue(e?.target?.value || '')}
                            type="text"
                            hasError={meta.error && meta.touched}
                          />
                          {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                        </div>
                      );
                    }}
                  </Field>
                )}
              </div>
              {request?.provider?.sensitive_information_options_for_patient_e_authorization && request?.fileData == undefined? (
                 <div className="row font-14">
                 <div className="col-12 col-md-12 mb-2 mt-3 d-flex">
                   <span className='mr-3'>
                   <InputLabel text={translate('text-sensitive-information')} />
                   </span>
                   <span className="d-flex" style={{ marginTop: '-4px' }} >
                     <Radio
                       name="sensitiveInformation"
                       value="Yes"
                       className="pr-2 ml-2 sensitive-radio-btn"
                       labelText="Yes"
                       checked={selectedValue === 'Yes'}
                       onChange={handleRadioChange}
                     />
                     <Radio
                       name="sensitiveInformation"
                       value="No"
                       className="pr-2 ml-2 sensitive-radio-btn"
                       labelText="No"
                       checked={selectedValue === 'No'}
                       onChange={handleRadioChange}
                     />
                   </span>
                 </div>
                 <div className="col-12 col-md-12 mb-3">
                   {sensitiveInformationOptions.map((option:any, index:any) => (
                     <div key={index}>
                       <input
                         type="checkbox"
                         value={option}
                         className="mr-2"
                         checked={selectedSensitiveInformationOptions.includes(option)}
                         onChange={() => handleCheckboxChange(option)}
                         disabled={selectedValue === 'No' || selectedValue === ''}
                       />
                       <label>{translate(option)}</label>
                     </div>
                   ))}
                 </div>
               </div>
              ) : null}
              <div className="row">
                <div className="col-12 my-3 d-flex align-items-center">
                  <InputLabel text={translate('text-type')} htmlFor="signatureSwitch" />
                  <CustomInput
                    type="switch"
                    id="signatureSwitch"
                    name="signatureSwitch"
                    className="ml-2 mb-1"
                    checked={isDrawMode}
                    onChange={e => {
                      setDrawMode(e.target.checked);
                      setSigned(false);
                    }}
                  />
                  <InputLabel text={translate('text-draw')} htmlFor="signatureSwitch" />
                </div>

                {isDrawMode ? (
                  <div className="col-12 col-md-6 my-3">
                    <InputLabel text={translate('text-preview')} />
                    <div className={styles.previewContainer}>
                      <SignatureCanvas
                        penColor="black"
                        canvasProps={{ className: styles.signCanvas }}
                        ref={signRef}
                        onBegin={() => setSigned(true)}
                      />
                    </div>

                    <div className='text-right mt-2'>
                    <span
                        role="button"
                        className="file-name"
                        onClick={() => {
                          clearSignature();
                        }}
                      >
                        Clear
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="col-12 col-md-6 my-3">
                    <InputLabel text={translate('text-preview')} />
                    <div className={styles.previewContainer}>
                      <div className="row w-100">
                        <div className="col-5 d-flex ml-5">
                          <div className={styles.signatureContainer}></div>
                          <div>
                            <div className={styles.head}>{translate('text-sign')}</div>
                            <div className={styles.signature} id="typeSignature">
                              {showExtraField ? extraValue : authName}
                            </div>
                          </div>
                        </div>
                        <div className="col-5 d-flex">
                          <div className={styles.signatureContainer}></div>
                          <div>
                            <div className={styles.head}>{translate('text-ds')}</div>
                            <div className={styles.signature}>{authInitials}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-12">
                  <div className="mt-3 mb-5">
                    <b>{translate('text-bottom')}</b>
                  </div>
                  <Button
                    className={styles.submit}
                    htmlType="submit"
                    disabled={
                      hasValidationErrors || 
                      (isDrawMode && !isSigned) || 
                      loading || 
                      ((request?.provider?.sensitive_information_options_for_patient_e_authorization && request?.fileData == undefined) ? selectedValue === '' : false)
                    }
                  >

                    {loading ? <Spinner className={'sm'} /> : translate('text-btn')}
                  </Button>
                </div>
              </div>
            </form>
          )}
        />
      </div>

      <div id="signatureNodeHTML" style={{ fontFamily: 'HomemadeApple', height: 0, width: 0, overflow: 'hidden' }}>
        {!isDrawMode && <canvas height="100" width="600" id="signatureCanvas"></canvas>}
      </div>
    </div>
  );
};